import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TiktokNotice } from '@/components/tiktok/TiktokShell';
import { errMsg } from '@/components/tiktok/workspace/workspaceShared';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { SearchInput } from '@/components/SearchInput';
import { DateRangePicker, DateRangeValue, describeRange } from '@/components/DateRangePicker';
import { LastSyncedBadge } from '@/components/LastSyncedBadge';
import { DailyBreakdownModal } from '@/components/tiktok/DailyBreakdownModal';
import { TiktokTrendChart } from '@/components/tiktok/TiktokTrendChart';
import {
  TabBody, TabToolbar, Segmented, KpiTile, Th, Td, Tr,
  SkeletonTable, SkeletonBlock, fmtMoney, fmtCount, fmtPct, resolveRange,
} from '@/components/tiktok/measurementShared';
import { tiktokService, TiktokInsightsRow, TiktokDailyRow } from '@/services/tiktok.service';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import {
  BarChart2, RefreshCw, ExternalLink, ChevronRight, Coins, Eye, MousePointerClick, Percent, Goal, TrendingUp,
} from 'lucide-react';
import toast from 'react-hot-toast';

type Level = 'campaign' | 'adgroup' | 'ad';

const LEVEL_OPTIONS: Array<{ value: Level; label: string }> = [
  { value: 'campaign', label: 'Campaigns' },
  { value: 'adgroup', label: 'Ad groups' },
  { value: 'ad', label: 'Ads' },
];

const LEVEL_NOUN: Record<Level, string> = { campaign: 'Campaign', adgroup: 'Ad group', ad: 'Ad' };

const SYNC_KEY = 'slightercrm.tiktok.report.syncedAt';

type Derived = TiktokInsightsRow & { ctr: number; cpc: number; cpa: number };
type SortKey = keyof Derived;

type Props = {
  /** Rendered inside a TabbedPage shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function TiktokReporting({ embedded = false }: Props = {}) {
  const [level, setLevel] = useState<Level>('campaign');
  const [range, setRange] = useState<DateRangeValue>({ preset: 'last_7d' });
  const [rows, setRows] = useState<TiktokInsightsRow[] | null>(null);
  const [daily, setDaily] = useState<TiktokDailyRow[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const request = useRef(0);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'spend', dir: 'desc' });
  const { currency } = useTiktokAccountCurrency();

  // Tracked client-side because the aggregate endpoint doesn't echo a fetchedAt.
  // Set on successful Sync; persists in localStorage so the badge survives navs.
  const [lastSync, setLastSync] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem(SYNC_KEY) : null,
  );

  const [drillEntity, setDrillEntity] = useState<TiktokInsightsRow | null>(null);

  const bounds = useMemo(() => resolveRange(range), [range]);

  const refresh = useCallback(async () => {
    const id = ++request.current;
    setLoadError('');
    const { from, to } = bounds;
    try {
      const [agg, day] = await Promise.all([
        tiktokService.aggregateReport({ level, from, to }),
        tiktokService.dailyTotals({ level, from, to }),
      ]);
      if (id !== request.current) return;
      setRows(agg); setDaily(day);
    } catch (e) {
      if (id !== request.current) return;
      setLoadError(errMsg(e)); setRows([]); setDaily([]);
    }
  }, [level, bounds]);

  useEffect(() => {
    setRows(null); setDaily(null);
    void refresh();
    return () => { request.current += 1; };
  }, [refresh]);

  const stampSync = () => {
    const now = new Date().toISOString();
    setLastSync(now);
    try { localStorage.setItem(SYNC_KEY, now); } catch { /* quota */ }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await tiktokService.syncReport({ from: bounds.from, to: bounds.to, level });
      toast.success(`Synced ${r.upserted} rows over ${r.days} days at ${r.level} level`);
      stampSync();
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setSyncing(false); }
  };

  // Auto-sync once on mount when the cache is empty or 10+ min stale, so the
  // dashboard isn't full of dashes the first time the user lands.
  useEffect(() => {
    const ageMs = lastSync ? Date.now() - Date.parse(lastSync) : Infinity;
    if (ageMs > 10 * 60 * 1000) {
      tiktokService.syncReport({ from: bounds.from, to: bounds.to, level })
        .then(() => { stampSync(); refresh(); })
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const derived = useMemo<Derived[]>(() => (rows ?? []).map((r) => ({
    ...r,
    ctr: r.impressions > 0 ? (r.clicks / r.impressions) * 100 : 0,
    cpc: r.clicks > 0 ? r.spend / r.clicks : 0,
    cpa: r.conversion > 0 ? r.spend / r.conversion : 0,
  })), [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? derived.filter((r) => r._id.toLowerCase().includes(q)) : derived;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [derived, query, sort]);

  const totals = useMemo(() => {
    const t = { impressions: 0, clicks: 0, spend: 0, conversion: 0, reach: 0, videoPlayActions: 0 };
    visible.forEach((r) => {
      t.impressions += r.impressions; t.clicks += r.clicks; t.spend += r.spend;
      t.conversion += r.conversion; t.reach += r.reach; t.videoPlayActions += r.videoPlayActions;
    });
    return t;
  }, [visible]);

  const loading = rows === null;
  const hasData = !!rows && rows.length > 0;
  const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
  const cpc = totals.clicks > 0 ? totals.spend / totals.clicks : 0;
  const dash = (s: string) => (hasData ? s : '—');

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === '_id' ? 'asc' : 'desc' }));
  const sortDir = (key: SortKey) => (sort.key === key ? sort.dir : null);

  const toolbar = (
    <>
      <Segmented value={level} onChange={setLevel} options={LEVEL_OPTIONS} />
      <DateRangePicker value={range} onChange={setRange} />
      <LastSyncedBadge timestamps={lastSync ? [lastSync] : []} />
      <Button size="sm" variant="secondary" onClick={() => refresh()} title="Reload from the local cache">
        <RefreshCw size={14} /> Refresh
      </Button>
      <Button size="sm" variant="primary" onClick={sync} loading={syncing} title="Pull fresh numbers from TikTok">
        <RefreshCw size={14} /> Sync from TikTok
      </Button>
    </>
  );

  return (
    <>
      {!embedded && (
        <Topbar
          title="Reporting"
          breadcrumb="TikTok Ads / Reporting"
          description="Cached performance metrics from TikTok's integrated report, per campaign, ad group or ad."
        />
      )}

      <TabBody>
        <TabToolbar
          title="Performance"
          subtitle={`${describeRange(range)} · ${LEVEL_NOUN[level]} performance`}
          right={toolbar}
        />

        {loadError && <TiktokNotice title="Could not load performance" error>{loadError}<Button size="sm" variant="ghost" onClick={refresh}>Retry</Button></TiktokNotice>}

        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <KpiTile label="Spend" icon={<Coins size={14} />} loading={loading} value={dash(fmtMoney(totals.spend, currency))} sub={currency ? `Account currency ${currency}` : undefined} />
          <KpiTile label="Impressions" icon={<Eye size={14} />} loading={loading} value={dash(fmtCount(totals.impressions))} sub="Times your ads were shown" />
          <KpiTile label="Clicks" icon={<MousePointerClick size={14} />} loading={loading} value={dash(fmtCount(totals.clicks))} sub={hasData ? `${fmtCount(totals.videoPlayActions)} video views` : undefined} />
          <KpiTile label="CTR" icon={<Percent size={14} />} loading={loading} value={dash(fmtPct(ctr))} sub="Clicks ÷ impressions" />
          <KpiTile label="CPC" icon={<TrendingUp size={14} />} loading={loading} value={dash(fmtMoney(cpc, currency))} sub="Spend ÷ clicks" />
          <KpiTile label="Conversions" icon={<Goal size={14} />} loading={loading} value={dash(fmtCount(totals.conversion))} sub={hasData && totals.conversion > 0 ? `${fmtMoney(totals.spend / totals.conversion, currency)} per conversion` : undefined} />
        </div>

        {/* Trend */}
        {(loading || hasData) && (
          <Card>
            <SectionHeader
              icon={<BarChart2 size={16} />}
              title="Daily trend"
              subtitle={`Account-wide totals per day · ${describeRange(range)}`}
            />
            {daily === null ? <SkeletonBlock height={224} /> : <TiktokTrendChart rows={daily} currency={currency ?? undefined} />}
          </Card>
        )}

        {/* Breakdown */}
        {loading && <SkeletonTable rows={6} cols={7} />}

        {!loading && !hasData && !loadError && (
          <EmptyState
            icon={<BarChart2 size={32} />}
            title="No metrics yet"
            description={
              `No ${LEVEL_NOUN[level].toLowerCase()}-level data for ${describeRange(range).toLowerCase()}. ` +
              'If your campaigns just launched, give TikTok a few hours to start reporting; ' +
              'otherwise check that you have running campaigns in TikTok Ads Manager.'
            }
            action={
              <>
                <Button onClick={sync} loading={syncing}><RefreshCw size={14} /> Sync now</Button>
                <Button variant="secondary" onClick={() => window.open('https://ads.tiktok.com/i18n/dashboard/campaign', '_blank', 'noopener')}>
                  <ExternalLink size={14} /> Open Ads Manager
                </Button>
              </>
            }
          />
        )}

        {hasData && (
          <Card padded={false} className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
              <div>
                <h3 className="font-display text-sm font-semibold tracking-tight text-text-primary">
                  {LEVEL_NOUN[level]} breakdown
                </h3>
                <p className="text-2xs text-text-muted">
                  {visible.length === derived.length
                    ? `${derived.length} ${derived.length === 1 ? 'row' : 'rows'}`
                    : `${visible.length} of ${derived.length} rows`} · click a row for the day-by-day view
                </p>
              </div>
              <SearchInput value={query} onChange={setQuery} placeholder={`Filter by ${LEVEL_NOUN[level].toLowerCase()} ID…`} />
            </div>

            {visible.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-text-muted">No rows match “{query}”.</div>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
                <table className="w-full border-separate border-spacing-0 text-sm" style={{ minWidth: 960 }}>
                  <thead>
                    <tr>
                      <Th onClick={() => toggleSort('_id')} sortDir={sortDir('_id')}>{LEVEL_NOUN[level]} ID</Th>
                      <Th align="right" onClick={() => toggleSort('spend')} sortDir={sortDir('spend')}>Spend</Th>
                      <Th align="right" onClick={() => toggleSort('impressions')} sortDir={sortDir('impressions')}>Impressions</Th>
                      <Th align="right" onClick={() => toggleSort('clicks')} sortDir={sortDir('clicks')}>Clicks</Th>
                      <Th align="right" onClick={() => toggleSort('ctr')} sortDir={sortDir('ctr')}>CTR</Th>
                      <Th align="right" onClick={() => toggleSort('cpc')} sortDir={sortDir('cpc')}>CPC</Th>
                      <Th align="right" onClick={() => toggleSort('conversion')} sortDir={sortDir('conversion')}>Conversions</Th>
                      <Th align="right" onClick={() => toggleSort('cpa')} sortDir={sortDir('cpa')}>CPA</Th>
                      <Th align="right" onClick={() => toggleSort('reach')} sortDir={sortDir('reach')}>Reach</Th>
                      <Th align="right" onClick={() => toggleSort('videoPlayActions')} sortDir={sortDir('videoPlayActions')}>Video views</Th>
                      <Th align="right" width="72px"><span className="sr-only">Details</span></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <Tr key={r._id} onClick={() => setDrillEntity(r)} title="Day-by-day breakdown">
                        <Td mono>{r._id}</Td>
                        <Td align="right" className="font-medium">{fmtMoney(r.spend, currency)}</Td>
                        <Td align="right">{fmtCount(r.impressions)}</Td>
                        <Td align="right">{fmtCount(r.clicks)}</Td>
                        <Td align="right" muted={r.impressions === 0}>{r.impressions > 0 ? fmtPct(r.ctr) : '—'}</Td>
                        <Td align="right" muted={r.clicks === 0}>{r.clicks > 0 ? fmtMoney(r.cpc, currency) : '—'}</Td>
                        <Td align="right">{fmtCount(r.conversion)}</Td>
                        <Td align="right" muted={r.conversion === 0}>{r.conversion > 0 ? fmtMoney(r.cpa, currency) : '—'}</Td>
                        <Td align="right">{fmtCount(r.reach)}</Td>
                        <Td align="right">{fmtCount(r.videoPlayActions)}</Td>
                        <Td align="right">
                          <span className="inline-flex items-center gap-0.5 text-2xs font-medium text-primary opacity-70 group-hover:opacity-100">
                            View <ChevronRight size={11} />
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
      </TabBody>

      {/* Day-by-day drill-down — reads from /reporting/daily (DB cache only). */}
      <DailyBreakdownModal
        open={!!drillEntity}
        onClose={() => setDrillEntity(null)}
        level={level}
        entityId={drillEntity?._id ?? ''}
        days={bounds.days}
        totalsHint={drillEntity ? {
          impressions: drillEntity.impressions,
          clicks: drillEntity.clicks,
          spend: drillEntity.spend,
          conversion: drillEntity.conversion,
        } : undefined}
      />
    </>
  );
}
